import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Group, Center, Loader, Text } from '@mantine/core';

interface WebsiteConfirmationProps {
  opened: boolean;
  websiteUrl: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function WebsiteConfirmationModal({ opened, websiteUrl, onConfirm, onCancel }: WebsiteConfirmationProps) {
  const iframeRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    if (opened && iframeRef.current) {
      iframeRef.current.focus();
    }

    if (opened) {
      setIframeError(false);

      const timeoutId = setTimeout(() => {
        // If it's still loading, assume an error occurred
        if (isLoading) {
          setIsLoading(false);
          setIframeError(true);
        }
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [opened, isLoading]);

  const handleIframeLoad = () => {
    iframeRef.current?.focus();
    setIsLoading(false);
    setIframeError(false);
  };

  const ErrorMessage = () => {
    return (
      <Center style={{ height: 200 }}>
        <Text c="red" fz="lg">
          There was an error loading the website.{'  '}
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
            Click here to view
          </a>
        </Text>
      </Center>
    );
  };

  return (
    <Modal
      ref={iframeRef}
      opened={opened}
      onClose={onCancel}
      title={
        <Text c="btb-navy" fz="xl" fw={700}>
          Please confirm your website, is this correct?
        </Text>
      }
      size="xl"
    >
      {isLoading && (
        <Center style={{ height: 200 }}>
          <Loader type="dots" />
        </Center>
      )}

      {!iframeError && (
        <iframe
          src={websiteUrl}
          title="website-preview"
          style={{ width: '100%', height: '600px', border: 'none' }}
          onLoad={() => handleIframeLoad}
          loading="lazy"
          onError={() => {
            setIframeError(true);
          }}
        />
      )}
      {iframeError && <ErrorMessage />}

      <Group justify="space-between">
        <Group mt="md">
          <Button variant="outline" onClick={onCancel}>
            No, let me change
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            Yes, it's correct
          </Button>
        </Group>
        <Text c="btb-navy">
          If the website is not viewable{' '}
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
            Click here to view
          </a>
        </Text>
      </Group>
    </Modal>
  );
}

export { WebsiteConfirmationModal };
